import React from "react";
import {
  Button,
  Grid,
  Group,
  Select,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { useUser } from "hooks/useUser";
import { useGetOrg } from "api/organizations/getOrg";
import { Client, useUpdateClient } from "api/client/updateClient";

type UpdateClientFormProps = {
  client: Client | null;
  onClose: () => void;
};

const updateClientSchema = z.object({
  name: z.string().min(1, "Please enter the client name"),
  email: z.string().email("Invalid email"),
  phone: z.string().min(10, "Phone # must be at least 10 digits"),
});

export default function UpdateClientForm({
  client,
  onClose,
}: UpdateClientFormProps) {
  const { user } = useUser();
  const { data: org } = useGetOrg(user.org?._id);
  const { mutate: updateClient, isLoading } = useUpdateClient();

  const form = useForm({
    initialValues: {
      name: client?.name || "",
      email: client?.email || "",
      phone: client?.phone || "",
      address: client?.address || "",
      notes: client?.notes || "",
      managedBy: client?.managedBy || "",
      description: client?.description || "",
    },
    validate: zodResolver(updateClientSchema),
    validateInputOnBlur: true,
  });

  if (!client) {
    return null;
  }

  const userSelectOptions = org?.members.map((u) => ({
    value: u._id,
    label: `${u.firstName} ${u.lastName}`,
  }));

  const handleSubmit = (values: typeof form.values) => {
    updateClient({ id: client._id, data: values });
    onClose();
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label="Name"
            withAsterisk
            {...form.getInputProps("name")}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Email"
            withAsterisk
            {...form.getInputProps("email")}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label="Phone #"
            withAsterisk
            {...form.getInputProps("phone")}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput label="Address" {...form.getInputProps("address")} />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={6}>
          <Select
            label="Managed By"
            data={userSelectOptions ?? []}
            {...form.getInputProps("managedBy")}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={12}>
          <Textarea
            label="Description"
            placeholder="Add any extra detail..."
            {...form.getInputProps("description")}
            minRows={2}
            autosize
          />
        </Grid.Col>
      </Grid>

      <Group position="right" spacing={20} mt={10}>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="filled"
          loading={isLoading}
          disabled={!form.isDirty()}
        >
          Update
        </Button>
      </Group>
    </form>
  );
}
